import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const FAQS = [
  {
    q: "What exactly is rent-to-own?",
    a: "You take possession of the vehicle and drive it for private use while fixed monthly payments build your ownership balance. Each payment is split openly into a rental & service component and an ownership allocation. At the end of your 48 or 60-month term — or earlier if you settle — the vehicle is registered in your name.",
  },
  {
    q: "Who qualifies?",
    a: "Employed and self-employed South Africans with a valid, un-suspended driver's licence and verifiable income. Our assessment is income-led: a weak credit history does not mean automatic rejection. Start with the two-minute pre-qualifier to see where you stand.",
  },
  {
    q: "Do I need a credit card?",
    a: "No. We accept debit order and EFT. You'll need a South African bank account in your name for the monthly debit order.",
  },
  {
    q: "What deposits are required?",
    a: "Two amounts, both disclosed upfront: an ownership deposit that comes straight off your ownership balance, and a refundable security deposit that is returned when the agreement ends in good standing. Exact amounts are vehicle-specific and shown in your written quotation before you sign.",
  },
  {
    q: "Is there a balloon payment at the end?",
    a: "No hidden balloon. Ever. Your term, your monthly amount and your ownership schedule are defined in writing before signature, and your early-settlement figure is always available on request.",
  },
  {
    q: "Who pays for insurance and maintenance?",
    a: "Vehicles are insured and tracked under approved arrangements included in your rental & service charge, and routine maintenance is covered as per your agreement. Your monthly statement shows exactly what you're paying for.",
  },
  {
    q: "Can I use the car for e-hailing, deliveries or business?",
    a: "No. Vehicles under the rent-to-own programme are for private personal and ordinary commuting use only — no e-hailing, courier, taxi or any carriage for reward, and no sub-renting.",
  },
  {
    q: "When do I officially own the car?",
    a: "When your ownership balance reaches zero — at the end of your scheduled term or through early settlement — transfer of ownership is completed and the vehicle is registered in your name.",
  },
  {
    q: "Can I settle early?",
    a: "Yes. You can request your early-settlement figure at any time and pay off the remaining ownership balance to take ownership sooner.",
  },
  {
    q: "What documents do I need to apply?",
    a: "Your ID, driver's licence, proof of income (payslips or bank statements) and your banking details for the debit order. You can upload ID, licence and proof of income directly in the online application.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <div data-testid="faq-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Straight answers</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-4">
            Frequently asked questions.
          </h1>
          <p className="text-base text-slate-400 max-w-2xl mb-14">
            Everything people ask us before they sign — answered the same way we
            answer it in the office. Still unsure? Call 010 534 9926.
          </p>
        </Reveal>

        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={Math.min(i * 0.05, 0.3)}>
              <div
                data-testid={`faq-item-${i}`}
                className={`border rounded-xl overflow-hidden transition-colors duration-300 ${
                  open === i ? "border-[#F57C00]/50 bg-[#122659]" : "border-sky-300/20 bg-[#122659]/60"
                }`}
              >
                <button
                  data-testid={`faq-question-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-base sm:text-lg font-bold text-white">{f.q}</span>
                  <Plus
                    size={20}
                    className={`shrink-0 text-[#F57C00] transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-sm text-slate-400 leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 rounded-2xl border border-[#F57C00]/30 bg-gradient-to-r from-[#F57C00]/15 via-[#122659] to-[#122659] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-display text-xl font-black text-white">Ready to check your eligibility?</p>
            <Link
              to="/pre-qualify"
              data-testid="faq-cta-btn"
              className="shrink-0 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-300"
            >
              Start pre-qualification
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
